import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useUserOptional } from "@/auth/UserContext";
import { api } from "@/services/api";
import type { SwitchableOrganization, UserProfile } from "@/types/api";

interface OrganizationContextValue {
  organization: string;
  organizations: readonly SwitchableOrganization[];
  currentClientKey: string | null;
  /** True when the user can switch into at least one other org. */
  canSwitch: boolean;
  switchOrganization: (auth0OrgId: string) => void;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

function currentOrgLabel(profile: UserProfile | null): string {
  return (
    profile?.organization_display_name ||
    profile?.organization_name ||
    profile?.client_key ||
    "Organization"
  );
}

function orgFromProfile(profile: UserProfile): SwitchableOrganization {
  return {
    auth0_org_id: "",
    client_key: profile.client_key,
    name: profile.organization_name || currentOrgLabel(profile),
    display_name: profile.organization_display_name,
    logo_url: profile.organization_logo_url,
  };
}

export function organizationLabel(org: SwitchableOrganization): string {
  return org.display_name || org.name;
}

/**
 * Current organization from `/v1/users/me`, plus switchable orgs for
 * SystemAdmin users (`/v1/users/me/organizations`). Switching re-logins
 * through Auth0 with the target `organization` id — same as frontend v1.
 */
export function OrganizationProvider({ children }: { children: ReactNode }) {
  const userCtx = useUserOptional();
  const profile = userCtx?.profile ?? null;
  const isSystemAdmin = userCtx?.isSystemAdmin ?? false;
  const switchToOrganization = userCtx?.switchOrganization;
  const [switchableOrgs, setSwitchableOrgs] = useState<SwitchableOrganization[]>(
    [],
  );

  useEffect(() => {
    if (!isSystemAdmin) {
      setSwitchableOrgs([]);
      return;
    }

    let cancelled = false;
    api
      .getMyOrganizations()
      .then((orgs) => {
        if (!cancelled) setSwitchableOrgs(orgs);
      })
      .catch(() => {
        if (!cancelled) setSwitchableOrgs([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isSystemAdmin]);

  const value = useMemo<OrganizationContextValue>(() => {
    const name = currentOrgLabel(profile);
    const organizations =
      switchableOrgs.length > 0
        ? switchableOrgs
        : profile
          ? [orgFromProfile(profile)]
          : [];

    return {
      organization: name,
      organizations,
      currentClientKey: profile?.client_key ?? null,
      canSwitch: organizations.length > 1,
      switchOrganization: (auth0OrgId: string) => {
        switchToOrganization?.(auth0OrgId);
      },
    };
  }, [profile, switchableOrgs, switchToOrganization]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const ctx = useContext(OrganizationContext);
  if (!ctx) {
    throw new Error("useOrganization must be used within OrganizationProvider");
  }
  return ctx;
}
